<script setup lang="ts">
import { computed } from "vue";

interface Props {
  color?: "neutral" | "neutral-dark-background" | "attention" | "danger" | "facebook" | "unilogin" | "wayf";
  size?: "small" | "large";
  tag?: "button" | "a" | "router-link";
  variant?: "solid" | "outline";
  disabled?: boolean;
  fullWidth?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  color: "neutral",
  size: "small",
  tag: "button",
  variant: "solid",
  fullWidth: false
});

const COMPONENT_CLASS = "le-button";

const variantClass = computed<string>(() => `${COMPONENT_CLASS}--variant-${props.variant}`);

const componentClasses = computed(() => [
  COMPONENT_CLASS,
  variantClass.value,
  props.fullWidth ? `${COMPONENT_CLASS}--full-width` : "",
  `${variantClass.value}--${props.color}`,
  `${COMPONENT_CLASS}--size-${props.size}`,
  {
    [`${COMPONENT_CLASS}--disabled`]: props.disabled
  }
]);

const componentType = computed<string | undefined>(() => (props.tag === "button" ? "button" : undefined));

const tabindex = computed<number | undefined>(() => {
  if (props.disabled) {
    return -1;
  }

  return undefined;
});
</script>

<template>
  <Component :is="props.tag" :type="componentType" :class="componentClasses" :disabled="disabled" :tabindex="tabindex">
    <span :class="`${COMPONENT_CLASS}__content`">
      <slot></slot>
    </span>
  </Component>
</template>

<style scoped lang="less">
@import "@dsk/styles/all.less";

@variant-map: {
  @solid: {
    @colors: {
      @neutral: {
        background: @gradients[@primary][default];
        color: @colors[@neutrals][100];
        focus: green;
        hover-background: @gradients[@primary][hover];
        has-focus-background: false;
        has-border: false;
      };
      @neutral-dark-background: {
        background: @gradients[@secondary][default];
        color: @colors[@primary][800];
        focus: green;
        hover-background: @gradients[@secondary][hover];
        has-focus-background: false;
        has-border: false;
      };
      @attention: {
        background: @colors[@accent][400];
        color: @colors[@accent][900];
        focus: yellow;
        hover-background: @colors[@accent][500];
        has-focus-background: false;
        has-border: false;
      };
      @danger: {
        background: @colors[@error][500];
        color: @colors[@neutrals][100];
        focus: red;
        hover-background: @colors[@error][400];
        has-focus-background: false;
        has-border: false;
      };
      @unilogin: {
        background: @colors[@third-party][unilogin];
        color: @colors[@neutrals][100];
        focus: unilogin;
        hover-background: darken(@colors[@third-party][unilogin], 5%);
        has-focus-background: false;
        has-border: false;
      };
      @wayf: {
        background: @colors[@third-party][wayf];
        color: @colors[@neutrals][100];
        focus: wayf;
        hover-background: darken(@colors[@third-party][wayf], 5%);
        has-focus-background: false;
        has-border: false;
      };
      @facebook: {
        background: @colors[@third-party][facebook];
        color: @colors[@neutrals][100];
        focus: facebook;
        hover-background: darken(@colors[@third-party][facebook], 5%);
        has-focus-background: false;
        has-border: false;
      };
    };
  };
  @outline: {
    @colors: {
      @neutral: {
        background: fade(@colors[@neutrals][100], 30%);
        color: @colors[@primary][800];
        focus: green;
        hover-background: @colors[@secondary][100];
        has-focus-background: true;
        has-border: true;
      };
      @neutral-dark-background: {
        background: fade(@colors[@primary][800], 30%);
        color: @colors[@neutrals][100];
        focus: green;
        hover-background: @colors[@primary][800];
        has-focus-background: true;
        has-border: true;
      };
      @attention: {
        background: fade(@colors[@neutrals][100], 30%);
        color: @colors[@accent][800];
        focus: yellow;
        hover-background: @colors[@accent][100];
        has-focus-background: true;
        has-border: true;
      };
      @danger: {
        background: fade(@colors[@neutrals][100], 30%);
        color: @colors[@error][800];
        focus: red;
        hover-background: @colors[@error][100];
        has-focus-background: true;
        has-border: true;
      };
    };
  };
};

.le-button {
  // Base style
  display: inline-block;
  transition: all 75ms ease-out;
  border: none;
  border-radius: 30px;
  font-weight: 700;
  text-align: center;

  &--disabled {
    opacity: 0.3;
    cursor: not-allowed;

    &:is(a:active) {
      pointer-events: none;
    }
  }

  &--full-width {
    width: 100%;
  }

  // Sizes
  &--size-small {
    padding: 10px 30px;
    font-size: 0.875rem;
    line-height: 1.25rem;
  }

  &--size-large {
    padding: 14px 40px;
    font-size: 1rem;
    line-height: 1.375rem;
  }

  &__content {
    display: grid;
    grid-auto-flow: column;
    align-items: center;
    column-gap: 8px;
  }

  // Colors
  each(@variant-map, {
    @variant: replace(@key, "@", "");

    &--variant-@{variant} {
      each(@value, {
        each(@value, {
          @color: replace(@key, "@", "");

          &--@{color} {
            background: @value[background];
            color: @value[color];
            border: 1px solid transparent;

            & when (@value[has-border] = true) {
              border-color: @value[color];
            }

            &:hover:not(.le-button--disabled) {
              background: @value[hover-background];
            }

            &.focus-visible {
              .focus-outline(@value[focus]);

              & when (@value[has-focus-background] = true) {
                background: @value[hover-background];
              }
            }
          }
        });
      });
    }
  });
}
</style>
